import { pgEnum } from 'drizzle-orm/pg-core'

/**
 * Lifecycle of a school's request to link one of its students to a parent's family account.
 *
 * Only `PENDING` is constrained to be unique per child — every terminal state is history, kept so the
 * school can see what happened and so a rejection can name its reason. A rejected or expired request
 * therefore returns the roster cell to "Sambungkan" rather than blocking the child forever.
 */
export const HomeConnectRequestStatusEnum = pgEnum('home_connect_request_status', [
  'PENDING',
  'ACCEPTED',
  'REJECTED',
  'EXPIRED',
  'CANCELLED',
])
export type HomeConnectRequestStatus = (typeof HomeConnectRequestStatusEnum.enumValues)[number]

/** A request in a terminal state: it no longer occupies the one-pending-per-child slot. */
export const HOME_CONNECT_RESOLVED_STATUSES = [
  'ACCEPTED',
  'REJECTED',
  'EXPIRED',
  'CANCELLED',
] as const satisfies readonly HomeConnectRequestStatus[]

/** How long a request stays actionable. Matches Better Auth's invitation default. */
export const HOME_CONNECT_REQUEST_TTL_DAYS = 7

/**
 * Lifecycle of a parent's request that a school release their child.
 *
 * `RESOLVED` means the school actually removed the student. `CANCELLED` covers every other way the
 * request stops mattering — the child's account going away, or the family link being dropped.
 * `EXPIRED` is the sweep. Only `PENDING` occupies the one-live-request slot, so a lapsed or answered
 * request returns the dialog to "Kirim permintaan" rather than blocking the parent forever.
 */
export const SchoolRemovalRequestStatusEnum = pgEnum('school_removal_request_status', [
  'PENDING',
  'RESOLVED',
  'EXPIRED',
  'CANCELLED',
])
export type SchoolRemovalRequestStatus = (typeof SchoolRemovalRequestStatusEnum.enumValues)[number]

/** How long a removal request stays live. Matches {@link HOME_CONNECT_REQUEST_TTL_DAYS}. */
export const SCHOOL_REMOVAL_REQUEST_TTL_DAYS = 7

/**
 * How many times one request may be re-sent. The parent cannot open a second request while one is
 * live, so this is the whole anti-spam budget: four mails to the school, then silence until the
 * request is answered or lapses.
 */
export const MAX_SCHOOL_REMOVAL_RESENDS = 3
